import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Select,
  Typography,
  App,
  Tooltip,
  Tag,
} from 'antd';
import {
  EyeOutlined,
  SendOutlined,
  SearchOutlined,
  ClearOutlined,
  DownOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { listPreliminaries, sendCompetitionToJudges } from '../../services/competitions';
import { useNavigate } from 'react-router-dom';
import { listEvaluationStatuses } from '../../services/catalogs';
import usePermissions from '../../hooks/usePermissions';
import { Dropdown } from 'antd';

const { Title } = Typography;
const { useApp } = App;

const STATUS_COLORS = {
  2: 'green',
  3: 'blue',
  4: '#BA955E',
  5: 'default',
};

const CompetitionPreliminaryEvaluationList = () => {
  const { message, modal } = useApp();
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);

  const { can } = usePermissions();

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  // filtros aplicados
  const [status, setStatus] = useState(null);

  // filtros draft
  const [draftStatus, setDraftStatus] = useState(null);

  useEffect(() => {
      const loadCatalogs = async () => {
        try {
          const [
            statusesData,
          ] = await Promise.all([
            listEvaluationStatuses(),
          ]);
          setStatuses(statusesData || []);
          
        } catch (error) {
          console.error('Error al cargar catálogos:', error);
          message.error('No se pudieron cargar los catálogos');
        }
      };
      loadCatalogs();
    }, [message]);

  const fetchData = useCallback(
    async (
      page,
      pageSize,
      srt,
      st
    ) => {
      setLoading(true);
      try {
        const params = {
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          sortField: srt?.field,
          sortOrder: srt?.order,
          ...(st && { statusEvaluationId: st }),
        };

        const { items, total } = await listPreliminaries(params);

        setData(items || []);
        setPagination(prev => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar las competencias');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = ({ status = null } = {}) => {
    setStatus(status);

    fetchData(
      1,
      pagination.pageSize,
      sorter,
      status
    );
  };

  const handleSearch = () => {
    applyFilters({ status: draftStatus });
  };

  const handleClearFilters = () => {
    setDraftStatus(null);
    applyFilters();
  };

  const onTableChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(
      pag.current,
      pag.pageSize,
      srt,
      status
    );
  };

 

  const renderActions = (record) => {
    if(!can('preliminary.approve')) return null;
    if (record.statusEvaluation?.description === 'Cerrado') return null;

    const items = [];

    const canEvaluate =
      (record.statusEvaluation?.description === 'Nuevo' ||
        record.statusEvaluation?.description === 'Proceso') &&
      record._count?.videos < record?.finalistLimit;

    if (canEvaluate) {
      items.push({
        key: 'evaluate',
        label: (
          <span>
            <EyeOutlined /> Evaluar
          </span>
        ),
      });
    }

    const canSend =
      record.statusEvaluation?.description === 'Terminado' ||
      record._count?.videos > 0;

    if (canSend) {
      items.push({
        key: 'send',
        label: (
            <span>
              <SendOutlined /> Terminar evaluación preliminar
            </span>
        ),
      });
    }

    if (!items.length) return null;
    return (
      <Dropdown
        menu={{ items, onClick: ({ key }) => handleMenuAction(key, record), }}
        trigger={['click']}
      >
        <Button type="primary">
          Acciones <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

   // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    switch (action) {
      case 'evaluate':
        navigate(`/evaluations/preliminary/${record.id}/evaluate`);
        break;
      case 'send':
        modal.confirm({
          title: '¿Terminar proceso de evaluación preliminar?',
          content: `Esta acción cerrará la etapa de evaluación preliminar y no podrá revertirse.`,
          okText: 'Sí, enviar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await sendCompetitionToJudges(record.id);
              message.success('La evaluación fue cerrada y enviada al jurado');
              handleSearch();
            } catch (error) {
              console.log(error)
              message.error(
                error?.response?.data?.error?.message ||
                'No se pudo terminar el proceso de evaluación preliminar'
              );
            }
          },
        });
        break;
      
      default:
        break;
    }
  };

  const columns = [
    {
      title: 'Competencia',
      dataIndex: 'folio',
      sorter: true,
    },
    {
      title: 'Modalidad',
      dataIndex: 'modalityId',
      render: (_, r) => r.modality?.name || r.modality?.description || '-',
    },
    {
      title: 'Videos finalistas',
      dataIndex: 'finalistLimit',
      sorter: true,
      align: 'center',
      render: (_, r) => (`${r._count?.videos || 0} de  ${_ || 0} `) || '-',
    },
    {
      title: 'Fecha de cierre',
      dataIndex: 'endDate',
      render: d => d ? moment(d).format('DD/MM/YYYY') : '-',
      sorter: true,
    },
    {
      title: 'Estatus',
      dataIndex: 'statusEvaluation',
      align: 'center',
      render: s => (
        <Tag color={STATUS_COLORS[s?.id] || 'default'}>
          {s?.description || '-'}
        </Tag>
      ),
    },
    {
      title: 'Acciones',
      align: 'center',
      render: (_, r) => renderActions(r),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Evaluación preliminar</Title>

      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Estatus"
            allowClear
            style={{ width: 200 }}
            value={draftStatus}
            onChange={setDraftStatus}
            options={statuses.map((status) => ({
                    value: Number(status.id),
                    label: `${status.description}`,
                  }))}
          />

          <Tooltip title="Buscar">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            />
          </Tooltip>

          <Tooltip title="Limpiar filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            />
          </Tooltip>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showTotal: t => `Total: ${t} competencias`,
          }}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
};

export default CompetitionPreliminaryEvaluationList;
